/* eslint-disable @nx/enforce-module-boundaries */
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import {
  Button,
  DesktopContainer,
  Input,
  LoadingSpinner,
  WarrantyInfo,
  ClaimExistsModal,
} from '@rabbit/elements/shared-components';
import {
  ConsumerIssueTypeHookOrigins,
  FileStorageContext,
  useConsumerHoldingEditor,
  useCreateCaseAsConsumer,
  useGetConsumerHoldings,
  useManageConsumerIssueTypes,
  useSendEmail,
  CaseFlow_Utils_CheckClaimExists,
} from '@rabbit/bizproc/react';
import { useMyConsumerPersona } from '@rabbit/data/portal';
import classNames from 'classnames';
import { OliveFileUploader } from '@rabbit/olive/components/organisms/upload-wrapper/OliveFileUploader';
import {
  CompletedUploadShape,
  DEFAULT_ACCEPTED_FILE_TYPES,
  DocTypeShapeTypes,
  UploadedFileCategories,
} from '@rabbit/elements/shared-types';
import { getCompletedUploadsOfCategory } from 'libs/bizproc/react/src/context/file-storage/helpers';
import { getWarrantyInfo } from '@rabbit/olive/utils/helpers';
import { ConsumerHoldingSummaryShape } from '@rabbit/bizproc/client';
import { useTranslation } from 'react-i18next';
import { OurDateTime } from '@rabbit/utils/ts';
import { InfoInterface } from '../warranty-info/HoldingSingleWarrantyInfoView';
import { AppContext } from '@rabbit/app-context';
import ContactDetailsRequiredModal from '../../../components/molecules/ContactDetailsRequired/ContactDetailsRequired';
import { useContactDetailsCheck } from '../../../hooks/useContactDetailsCheck';


export interface HoldingSingleClaimViewProps {
  holding_id?: string;
  // If this is defined, then the user is on the NLIC flow
  onChangeStep?: (data: any) => void;
}

interface FormValuesShape {
  issue: string;
  additional_info: string;
}

export function HoldingSingleClaimView({
  holding_id,
  onChangeStep,
}: HoldingSingleClaimViewProps) {
  const { t } = useTranslation();
  const tenantLink = t('tenantLink');
  const [warrantyInfo, setWarrantyInfo] = useState<InfoInterface[]>([]);
  const { config, tenantInfo } = useContext(AppContext);

  const schema = Yup.object({
    issue: Yup.mixed().required('Please select an issue'),
    additional_info: Yup.string(),
    id: Yup.string(),
  });

  const { SE_Olive_Claim_Waiting_For_Review } = useSendEmail();
  const navigate = useNavigate();
  const location = useLocation();
  const formikRef = useRef<FormValuesShape>() as any;
  const holdingId = holding_id ?? location.pathname.split('/')[2];
  const draftKey = useMemo(() => `olive_claim_draft:${holdingId}`, [holdingId]);
  const { createCase, serverCreateCase } = useCreateCaseAsConsumer(holdingId);
  const persona = useMyConsumerPersona();
  const contactGate = useContactDetailsCheck({
    consumerPersonaId: persona?.personaId,
  });
  const { body } = useConsumerHoldingEditor(
    persona?.personaId ?? '',
    holdingId
  );

  const { consumerIssueTypeList } = useManageConsumerIssueTypes(
    ConsumerIssueTypeHookOrigins.OLIVE
  );
  const consumerIssueTypeOptions = !consumerIssueTypeList?.data
    ? []
    : [...consumerIssueTypeList.data]
        .sort((a, b) =>
          (a.label || '').localeCompare(b.label || '', undefined, {
            sensitivity: 'base',
          })
        )
        .map((issue) => ({
          id: issue.docid,
          value: issue.docid,
          label: issue.label,
        })) ?? [];

  const { getSingleHolding } = useGetConsumerHoldings(persona?.personaId || '');

  const [holdingSummary, setHoldingSummary] =
    useState<ConsumerHoldingSummaryShape>({} as ConsumerHoldingSummaryShape);

  const [initialValues, setInitialValues] = useState<FormValuesShape>({
    issue: '',
    additional_info: '',
  });

  // Load any saved draft from sessionStorage
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(draftKey);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<FormValuesShape>;
        setInitialValues((prev) => ({
          issue: parsed.issue ?? prev.issue,
          additional_info: parsed.additional_info ?? prev.additional_info,
        }));
      }
    } catch (err) {
      // ignore draft load errors
    }
  }, [draftKey]);

  useEffect(() => {
    const getWarranty = async () => {
      // Pass brand information for brand-specific warranty terms
      // Only pass brand if it's a valid non-empty string
      const brand = holdingSummary.brand && typeof holdingSummary.brand === 'string' && holdingSummary.brand.trim() !== ''
        ? holdingSummary.brand
        : undefined;
      const warranty = await getWarrantyInfo(t('tenantLink'), brand);
      if (warranty) {
        setWarrantyInfo(warranty.warranty_info);
      }
    };
    void getWarranty();
  }, [t, holdingSummary.brand]);

  useEffect(() => {
    (async () => {
      const holding = await getSingleHolding(holdingId);
      if (holding) {
        setHoldingSummary(holding);
        // Only reset defaults if there is no saved draft
        const hasDraft = !!sessionStorage.getItem(draftKey);
        if (!hasDraft) {
          setInitialValues({
            issue: '',
            additional_info: '',
          });
        }
      }
    })().catch((err) => console.error(err));
  }, [persona?.personaId, draftKey]);

  const { uploadQueueState, moveCompletedUploadsToAttached, isUpdating } =
    useContext(FileStorageContext) || {};

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showExistingClaimModal, setShowExistingClaimModal] = useState(false);
  const [pendingValues, setPendingValues] = useState<FormValuesShape | null>(null);


  if (!body) return <LoadingSpinner size="md" extraClasses="mt-5" />;

  // const selfRegistered = body?.public?.self_registration ?? false;
  // // Disabled until a database of manufacturers and their claim urls is created
  // const manufacturer_claim_url = undefined; // "https://google.com/product/claim"
  // const manufacturer_name = body?.public?.self_registration?.brand ?? null;
  // const warrantor = holdingSummary.warranties
  //   ? BL_Warranty.getLatestWarranty(
  //       holdingSummary.warranties
  //     )?.warrantorLink?.split(':')[1] || undefined
  //   : undefined;

  const onConfirmNewClaim = async () => {
    if (!pendingValues) return;
    const values = pendingValues;
    setShowExistingClaimModal(false);
    setPendingValues(null);
    // Pass supersede as an argument, not via setState: the state written here is
    // not visible to this call's closure, and re-running the existence check
    // would just re-open the modal.
    await createClaim(values, true);
  };

  const onKeepExistingClaim = () => {
    setShowExistingClaimModal(false);
    setPendingValues(null);
    setIsSubmitting(false);
  };

  const createClaim = async (
    values: FormValuesShape,
    supersedePreviousClaim = false
  ) => {
    // Block claim creation if mandatory contact details are missing (covers deep-linking to claim page).
    const result = await contactGate.ensureContactDetails();
    if (!result.ok) return;

    setIsSubmitting(true);
    const { completed } = uploadQueueState ?? {};

    const caseEvidenceUploads: CompletedUploadShape[] =
      getCompletedUploadsOfCategory(
        completed ?? [],
        UploadedFileCategories.ConsumerCaseEvidence
      );

    const caseEvidenceFiles = completed?.flatMap((file) =>
      file.category === UploadedFileCategories.ConsumerCaseEvidence &&
      file?.uploadedFile
        ? [file.uploadedFile]
        : []
    );

    try {
      let res: string | null | undefined | Error = null;
      if (!supersedePreviousClaim) {
        const claimExists = await CaseFlow_Utils_CheckClaimExists(holdingId);
        if (claimExists.hasActiveClaim) {
          setPendingValues(values);
          setShowExistingClaimModal(true);
          setIsSubmitting(false);
          return;
        }
      }

      // if we have a holding id, it's the normal logged in claim, and we use the serverCreateCase function (aka do it from the server)
      // eventually we want to do it all from the server
      if (holdingId) {
        try {
          res = await serverCreateCase({
            consumer_issue_type: consumerIssueTypeOptions.filter(
              (option) => option.value === values.issue
            )[0].label,
            consumer_issue_type_ref: values.issue,
            consumer_issue_description: values.additional_info,
            consumer_proof_of_purchase: body?.private?.receipt?.length
              ? body?.private?.receipt
              : [],
            consumer_claim_evidence: caseEvidenceFiles?.length
              ? caseEvidenceFiles
              : [],
            serial_number: body?.private?.serial,
            serial_number_proof: body?.private?.serial_proof,
            // This flag tells the server to lock the previous claim
            supersede_old_claim: supersedePreviousClaim,
          });
        } catch (err: any) {
          const errorMsg = err?.message as string;
          console.error(err);
          toast.error(t('An error occurred while creating your claim. Please try again later.'));
        }
      } else {
        // if we're on the NLIC flow, we use the old createCase function (aka do it from the client)
        res = await createCase({
          consumer_issue_type: consumerIssueTypeOptions.filter(
            (option) => option.value === values.issue
          )[0].label,
          consumer_issue_type_ref: values.issue,
          consumer_issue_description: values.additional_info,
          consumer_proof_of_purchase: body?.private?.receipt?.length
            ? body?.private?.receipt
            : [],
          consumer_claim_evidence: caseEvidenceFiles?.length
            ? caseEvidenceFiles
            : [],
        });
      }

      const case_time_created = OurDateTime.nowUTCTimestamp();

      // Don't do anything else if the create functions fail
      if (!res) {
        return;
      }
      const claimURL = window.location.origin + '/repairs/' + res;
      const emailToRaw = persona.data.consumer_private?.email ?? '';
      const firstNameRaw = persona.data.consumer_private?.splitname?.first ?? '';
      const fullNameRaw = persona.data.consumer_private?.fullname ?? '';
      const tenantLinkRaw = tenantLink;
      const productNameRaw = holdingSummary?.title ?? '';

        // Important: `??` does NOT treat '' (empty string) as "missing", so we must trim + use `||` style fallback.
        const emailTo = emailToRaw.trim();
        const consumerName =
          firstNameRaw.trim() || fullNameRaw.trim() || emailTo;
        const productName = productNameRaw.trim();

        // Non-blocking: claim creation succeeded; email send should never break the rest of the flow.
        try {
          await SE_Olive_Claim_Waiting_For_Review(
            emailTo,
            consumerName,
            res as string,
            productName,
            case_time_created,
            claimURL,
            tenantLinkRaw
          );
        } catch (err) {
          console.warn(
            'Claim created but claim-waiting-review email failed',
            err
          );
        }

      // After creating the case, move the claim evidence files to attached so they don't get cleaned up
      if (moveCompletedUploadsToAttached) {
        if (caseEvidenceFiles && caseEvidenceFiles.length > 0) {
          moveCompletedUploadsToAttached(caseEvidenceUploads);
        }
      }

      // Clear any saved draft on success
      try { sessionStorage.removeItem(draftKey); } catch {}
      if (!onChangeStep) {
        toast.success(t('Created your claim successfully!'));
        navigate('/repairs/' + res);
      } else if (onChangeStep) {
        onChangeStep({
          ...values,
          date_of_claim: new Date(),
          claimId: res,
          purchase_date: body?.public?.purchase_time,
          email: persona.data.consumer_private?.email,
        });
      }
    } catch (e: any) {
      const errorMsg = (e?.message as string) ?? String(e);
      console.error(e);
      if (errorMsg.includes('Case already exists')) {
        toast.error(
          t(
            "Claim already exists for this product. "
          )
        );
      } else {
        toast.error(t('An error occurred while creating your claim. Please try again.'));
      }
      return;
    } finally {
      setIsSubmitting(false);
    }
  };

  const innerComponent = () => {
    return (
      <>
      <div
        className={classNames('flex flex-col', {
          'mt-5': onChangeStep,
        })}
      >
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={schema}
          // Wrapped: Formik passes FormikHelpers as arg 2, which would arrive
          // as a truthy supersedePreviousClaim on every ordinary submit.
          onSubmit={(values) => createClaim(values)}
          innerRef={formikRef}
        >
          {({ errors, values }) => {
            return (
              <Form
                className="font-nunito flex flex-col gap-5"
                onChange={() => {
                  try {
                    sessionStorage.setItem(draftKey, JSON.stringify(values));
                  } catch (err) {
                    // ignore save errors
                  }
                }}
              >
                <Input
                  type="select"
                  name="issue"
                  label={t('Please select an issue*')}
                  settings={{
                    options: consumerIssueTypeOptions,
                    placeholder: t('Select'),
                    id: 'issue',
                    hint: '*required',
                  }}
                />
                <Input
                  label={t('Additional information')}
                  type="text"
                  name="additional_info"
                  settings={{
                    id: 'additional_info',
                    placeholder: t('Write text here…'),
                    allowSpecialCharacter: true,
                  }}
                />

                <div>
                  {tenantLink === 'SHELTA' && (
                    <OliveFileUploader
                      label={t('Proof of purchase')}
                      identifiers={{
                        category: UploadedFileCategories.ConsumerProofPurchase,
                        docType: {
                          docid: holdingId,
                          type: DocTypeShapeTypes.Holding,
                        },
                        personaId: persona?.personaId ?? '',
                      }}
                      shouldAutoUpdateDocs={true}
                      accepts={DEFAULT_ACCEPTED_FILE_TYPES}
                    />
                  )}
                  <OliveFileUploader
                    label={t('Supporting Media')}
                    currentFiles={[]}
                    identifiers={{
                      category: UploadedFileCategories.ConsumerCaseEvidence,
                      docType: {
                        docid: holdingId,
                        type: DocTypeShapeTypes.Case,
                      },
                      personaId: persona?.personaId ?? '',
                    }}
                    accepts={DEFAULT_ACCEPTED_FILE_TYPES}
                  />
                </div>
                <Button
                  kind="primary"
                  type="submit"
                  disabled={
                    Object.keys(errors).length > 0 || isSubmitting || isUpdating
                  }
                  loading={isSubmitting}
                >
                  Submit
                </Button>
              </Form>
            );
          }}
        </Formik>
      </div>
      {showExistingClaimModal && (
        <ClaimExistsModal 
          isOpen={showExistingClaimModal}
          onClose={onKeepExistingClaim}
          onConfirm={onConfirmNewClaim}
          isSubmitting={isSubmitting}
        />
      )}
    </>
    );
  };

  if (!holdingSummary) return <LoadingSpinner size="md" extraClasses="mt-5" />;

  const contactDetailsModal = contactGate.showModal ? (
    <ContactDetailsRequiredModal
      isOpen={contactGate.showModal}
      onClose={contactGate.closeModal}
      consumerPersonaId={persona?.personaId ?? ''}
      redirectTo={`${window.location.pathname}`}
      missingAddress={contactGate.missingAddress}
      missingPhoneNumber={contactGate.missingPhoneNumber}
    />
  ) : null;

  if (onChangeStep) return (
    <>
      {innerComponent()}
      {contactDetailsModal}
    </>
  );

  return config.CLAIMS.CLAIMS_FLOW.CREATE_WITH_WARRANTY_INFO ? (
    <>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <DesktopContainer
          title={t('Initiate claim')}
          goBack={() => navigate(-1)}
          loading={isSubmitting}
        >
          {innerComponent()}
        </DesktopContainer>
        <div className="h-[566px] overflow-y-auto rounded-lg md:shadow-xl">
          {warrantyInfo?.length > 0 && (
            <div className="md:px-8 md:py-8">
              <div className="hidden text-center md:block">
                <div className="font-caudex mb-6 text-xl font-bold md:text-2xl">
                  {tenantInfo?.name === 'NuCover'
                    ? t('general.carePlanSummary')
                    : t('general.warrantyInformation')}
                </div>
              </div>
              {warrantyInfo &&
                warrantyInfo.map((data: InfoInterface, index: number) => {
                  return <WarrantyInfo key={index} data={data} />;
                })}
            </div>
          )}
        </div>
      </div>
      {contactDetailsModal}
    </>
  ) : (
    <>
      <DesktopContainer
        title={t('Initiate claim')}
        goBack={() => navigate(-1)}
        loading={isSubmitting}
      >
        {innerComponent()}
      </DesktopContainer>
      {contactDetailsModal}
    </>
  );
}

export default HoldingSingleClaimView;
